import {
  OFFER_ENTITLEMENTS,
  type EffectiveEntitlements,
  type FeatureEntitlements,
  type LimitValue,
  type OfferId,
  type PlanId,
} from './entitlements';

/**
 * 上限に達したときの「プラン変更」導線で、どのプランへ誘導できるかを決める。
 * 誘導先は、いま購入できるプランのうち、その上限が実際に増えるプランだけ。
 */

export type UpgradeLimitKind =
  | 'members'
  | 'storage'
  | 'creditsInternal'
  | 'creditsPublic';

/**
 * 画面から購入できるプラン。standard は販売前、NCN の team は非表示のため含めない。
 * 販売を始めたらここへ足す（UpgradeModal の表示と揃えること）。
 */
export const PURCHASABLE_PLAN_IDS: readonly PlanId[] = ['starter'];

/** 下位ほど先。現在より上のプランだけを候補にする */
const PLAN_ORDER: readonly PlanId[] = ['free', 'starter', 'standard', 'team', 'exclusive'];

/** 上限値。無効な機能は 0 とみなす（増えたかの比較のため）。null は無制限 */
function readLimit(features: FeatureEntitlements, kind: UpgradeLimitKind): LimitValue {
  switch (kind) {
    case 'members': {
      const f = features.members;
      if (!f?.enabled) return 0;
      return f.limits?.limit ?? null;
    }
    case 'storage': {
      const f = features.storage;
      if (!f?.enabled) return 0;
      return f.limits?.limitBytes ?? null;
    }
    case 'creditsInternal': {
      const f = features['credits.internalRag'];
      if (!f?.enabled) return 0;
      return f.limits?.monthlyCredits ?? 0;
    }
    case 'creditsPublic': {
      const f = features['credits.publicRag'];
      if (!f?.enabled) return 0;
      return f.limits?.monthlyCredits ?? 0;
    }
  }
}

/** next が current より大きいか。null は無制限 */
function isLarger(next: LimitValue, current: LimitValue): boolean {
  if (current === null) return false;
  if (next === null) return true;
  return next > current;
}

/**
 * 現在の Effective Entitlements から、上限が増える最小の購入可能プランを返す。
 * 該当がなければ null（その場合、画面ではボタンを出さない）。
 */
export function findUpgradePlan(
  effective: EffectiveEntitlements | null,
  kind: UpgradeLimitKind
): PlanId | null {
  if (!effective) return null;
  const currentIndex = PLAN_ORDER.indexOf(effective.planId);
  const currentLimit = readLimit(effective.features, kind);

  for (const planId of PLAN_ORDER) {
    if (PLAN_ORDER.indexOf(planId) <= currentIndex) continue;
    if (!PURCHASABLE_PLAN_IDS.includes(planId)) continue;
    const offerId = `${effective.seriesId.toLowerCase()}:${planId}` as OfferId;
    const offer = OFFER_ENTITLEMENTS[offerId];
    if (!offer) continue;
    if (isLarger(readLimit(offer, kind), currentLimit)) return planId;
  }
  return null;
}
